import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useTranslation, SUPPORTED_LANGUAGES } from "@/hooks/useTranslation";
import {
  Bird, Truck, HardHat, LayoutDashboard, Eye, ChevronRight,
  Shield, Zap, BarChart3, Globe, Users, Clock, Check, ArrowRight,
  Star, Mail, FileText, Lock, Award, Fuel, MapPin, MessageSquare, Cookie, X
} from "lucide-react";

const FEATURES = [];

const EDITORIAL_IMAGES = {
  field: "https://images.unsplash.com/photo-1694521787162-5373b598945c?w=800&q=80",
  decision: "https://images.unsplash.com/photo-1666660165383-0c63c6947e46?w=800&q=80",
  birdseye: "https://images.pexels.com/photos/8782658/pexels-photo-8782658.jpeg?auto=compress&cs=tinysrgb&w=800",
  freeflow: "https://images.unsplash.com/photo-1627054458477-40ba0148b76a?w=800&q=80",
};

const PRICING = [
  { id: "training", name: "Training & Demo", price: 0, currency: "EUR", period: "free", features: ["Demo Hub Access", "Interactive Tutorials", "TTS Voice Narration", "All Role Previews", "Up to 1 Hub", "3 Sites / 10 Machines", "Basic Request Flow"], highlight: false, cta: "Start Free", locked: [] },
  { id: "basic", name: "Basic", price: 49, currency: "EUR", period: "/mo", features: ["14-Day Free Trial", "3 Hubs", "5 Drivers per Hub", "Unlimited Sites & Machines", "Fuel Request Highlights", "PDF Data Import (Philogic AI)", "PDF Log Export", "Calendar & Appointments", "Basic Route Planning", "Email Support"], highlight: true, cta: "Start Free Trial", locked: ["AI Bulk Machine Import", "Route History & Reuse", "Transport Map View", "Toolbox Uploads", "Driver Leave Management"] },
  { id: "premium", name: "Premium", price: 149, currency: "EUR", period: "/mo", features: ["Everything in Basic", "Unlimited Hubs & Drivers", "AI Bulk Machine Import", "Route History & Reuse", "Transport Live Map", "Toolbox Document Uploads", "Driver Leave Management", "Service & Transport Portals", "Site Phase Tracking", "Priority Support", "GDPR Compliance Pack"], highlight: false, cta: "Go Premium", locked: [] },
];

export default function LandingPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const pricingRef = useRef(null);
  const [cookieConsent, setCookieConsent] = useState(() => localStorage.getItem("pigeon-cookie-consent"));
  const [selectedLang, setSelectedLang] = useState(() => localStorage.getItem("pigeon-lang") || null);
  const [showZeroGate, setShowZeroGate] = useState(() => !localStorage.getItem("pigeon-lang"));
  const { t } = useTranslation(selectedLang || "en");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }
    setIsCheckingPayment(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        setIsCheckingPayment(false);
        return;
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        setIsCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      if (attempts < 4) setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      else { setPaymentStatus("error"); setIsCheckingPayment(false); }
    }
  };

  const handleCheckout = async (packageId) => {
    try {
      const origin = window.location.origin;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout?package_id=${packageId}&origin_url=${encodeURIComponent(origin)}`, { method: 'POST' });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error("Checkout error:", e);
    }
  };

  if (activePage === "terms") return <LegalPage title="Terms of Service" onBack={() => setActivePage("home")} content={<TermsContent />} />;
  if (activePage === "privacy") return <LegalPage title="Privacy Policy" onBack={() => setActivePage("home")} content={<PrivacyContent />} />;
  if (activePage === "refund") return <LegalPage title="Refund Policy" onBack={() => setActivePage("home")} content={<RefundContent />} />;
  if (activePage === "contact") return <LegalPage title="Contact Us" onBack={() => setActivePage("home")} content={<ContactContent />} />;
  if (activePage === "gdpr") return <LegalPage title="GDPR & Cookie Policy" onBack={() => setActivePage("home")} content={<GDPRContent />} />;

  // Zero Gate - Language Selection
  if (showZeroGate) {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6 relative overflow-hidden" data-testid="zero-gate">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.03)_0%,transparent_70%)]" />
        <div className="relative z-10 w-full max-w-lg text-center animate-fadeInUp">
          <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
            <Bird className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tighter text-slate-900 mb-3">
            <span className="font-light">Pigeon</span> <span className="font-black">Supply</span>
          </h1>
          <p className="text-base text-slate-500 font-light mb-10">
            {t("choose_language")}
          </p>
          <div className="grid grid-cols-3 gap-3 mb-8">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                data-testid={`zero-gate-lang-${lang.code}`}
                onClick={() => setSelectedLang(lang.code)}
                className={`p-4 rounded-xl text-center transition-all border-2 text-sm tracking-tight ${
                  selectedLang === lang.code
                    ? "bg-red-50 border-red-500 text-red-700 shadow-sm font-bold"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 font-medium"
                }`}
              >
                <span className="block text-xs font-black mb-0.5 opacity-40">{lang.flag}</span>
                <span className="block text-sm">{lang.label}</span>
              </button>
            ))}
          </div>
          <button
            data-testid="zero-gate-continue-btn"
            onClick={() => {
              const lang = selectedLang || "en";
              localStorage.setItem("pigeon-lang", lang);
              document.documentElement.lang = lang;
              document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
              setSelectedLang(lang);
              setShowZeroGate(false);
            }}
            className="btn-industrial w-full py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-black shadow-lg transition-all"
          >
            {t("continue_lang")} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white text-slate-900" data-testid="landing-page">
      {/* Payment Status Banner */}
      {paymentStatus === "success" && (
        <div className="bg-red-600 text-slate-950 py-4 px-6 text-center font-bold text-sm uppercase tracking-wider animate-slideInDown" data-testid="payment-success">
          <Check className="w-5 h-5 inline mr-2" /> {t("payment_success")}
        </div>
      )}
      {isCheckingPayment && (
        <div className="bg-amber-500 text-slate-950 py-3 px-6 text-center font-bold text-sm uppercase tracking-wider animate-pulse">
          {t("verifying_payment")}
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-md">
              <Bird className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <span className="text-lg font-black tracking-tighter uppercase">Pigeon Supply</span>
              <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-slate-400">{t("supply_platform")}</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors">{t("nav_pricing")}</button>
            <button onClick={() => nav("/install")} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors flex items-center gap-1" data-testid="nav-install-btn">{t("install_app")}</button>
            <button onClick={() => setActivePage("contact")} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors">{t("nav_contact")}</button>
            <button data-testid="nav-launch-btn" onClick={() => nav("/auth")} className="btn-industrial px-5 py-2.5 bg-red-600 text-white rounded-xl text-xs font-black shadow-sm hover:bg-red-500 transition-colors">
              {t("launch_app")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero — Editorial */}
      <section className="relative overflow-hidden pt-24 pb-32 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.04)_0%,transparent_50%)]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-10 animate-fadeInUp" style={{ opacity: 0 }}>
            <Award className="w-4 h-4 text-red-500" />
            <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{t("hero_badge")}</span>
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.85] mb-8 animate-fadeInUp stagger-1" style={{ opacity: 0 }}>
            <span className="font-light text-slate-400">{t("hero_line1")}</span><br />
            <span className="font-black text-slate-900">{t("hero_line2")}</span>{" "}
            <span className="font-black text-red-600">{t("hero_line3")}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl font-light leading-relaxed mb-12 animate-fadeInUp stagger-2" style={{ opacity: 0 }}>
            {t("hero_desc")}
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4 animate-fadeInUp stagger-3" style={{ opacity: 0 }}>
            <button data-testid="hero-start-btn" onClick={() => nav("/auth")}
              className="btn-industrial px-10 py-5 bg-red-600 text-white rounded-xl text-sm font-black shadow-lg hover:bg-red-500 hover:shadow-xl transition-all">
              {t("start_free_trial")} <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="btn-industrial px-10 py-5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl text-sm font-black hover:border-red-300 transition-all">
              {t("view_pricing")}
            </button>
          </div>
        </div>
      </section>

      {/* Social Proof — Red accent */}
      <section className="py-14 border-y border-slate-200 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "150+", label: t("stat_hubs") },
              { value: "1,200+", label: t("stat_drivers") },
              { value: "50M+", label: t("stat_tracked") },
              { value: "9", label: t("stat_languages") },
            ].map((stat, i) => (
              <div key={i} className="animate-fadeInUp" style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}>
                <span className="text-3xl md:text-4xl font-black text-red-600 font-mono block">{stat.value}</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2 block">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1 — Assess the Situation */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Small tilt-shift image — aligned left */}
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden rounded-2xl shadow-xl w-full aspect-[4/5]">
              <img src={EDITORIAL_IMAGES.field} alt="Construction site with heavy machinery where Pigeon Supply coordinates fuel and material deliveries in real time" className="w-full h-full object-cover saturate-[1.3]" style={{ filter: "contrast(1.1)" }} />
              <div className="absolute inset-0 pointer-events-none" style={{
                backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)",
                maskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)"
              }} />
            </div>
            <div className="absolute -bottom-3 -right-3 w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <HardHat className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
          </div>
          {/* Text — right side */}
          <div className="lg:col-span-7 pt-4">
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{t("section1_tag")}</span>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1] mb-8">
              <span className="font-extralight text-slate-400">{t("section1_line1")}</span><br />
              <span className="font-black text-slate-900">{t("section1_line2")}</span>
            </h2>
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-5 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section1_p1") }} />
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-8 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section1_p2") }} />
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-bold text-red-600 uppercase tracking-wider">Machinist App</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Echo Tank</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Driver Feedback</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Command with Confidence (reversed) */}
      <section className="py-24 px-6 bg-slate-50/50 overflow-hidden">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Text — left side */}
          <div className="lg:col-span-7 order-2 lg:order-1 pt-4">
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{t("section2_tag")}</span>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1] mb-8">
              <span className="font-extralight text-slate-400">{t("section2_line1")}</span><br />
              <span className="font-black text-slate-900">{t("section2_line2")}</span>{" "}
              <span className="font-black text-red-600">{t("section2_line3")}</span>
            </h2>
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-5 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section2_p1") }} />
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-8 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section2_p2") }} />
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-bold text-red-600 uppercase tracking-wider">Hub Invitations</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Contractor Assignment</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Instant Checks</span>
            </div>
          </div>
          {/* Image — right */}
          <div className="lg:col-span-5 relative order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-2xl shadow-xl w-full aspect-[4/5]">
              <img src={EDITORIAL_IMAGES.decision} alt="Strategic logistics operations showing dispatchers coordinating supply deliveries across construction sites" className="w-full h-full object-cover saturate-[1.3]" style={{ filter: "contrast(1.1)" }} />
              <div className="absolute inset-0 pointer-events-none" style={{
                backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)",
                maskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)"
              }} />
            </div>
            <div className="absolute -bottom-3 -left-3 w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Zap className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Bird's Eye View */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Image — left */}
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden rounded-2xl shadow-xl w-full aspect-[4/5]">
              <img src={EDITORIAL_IMAGES.birdseye} alt="Aerial bird's eye view of a construction fleet and logistics operations managed by Pigeon Supply" className="w-full h-full object-cover saturate-[1.4]" style={{ filter: "contrast(1.15)" }} />
              <div className="absolute inset-0 pointer-events-none" style={{
                backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)",
                maskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)"
              }} />
            </div>
            <div className="absolute -top-3 -right-3 w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Bird className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
          </div>
          {/* Text — right */}
          <div className="lg:col-span-7 pt-4">
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{t("section3_tag")}</span>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1] mb-8">
              <span className="font-extralight text-slate-400">{t("section3_line1")}</span><br />
              <span className="font-black text-slate-900">{t("section3_line2")}</span>{" "}
              <span className="font-black text-red-600">{t("section3_line3")}</span>
            </h2>
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-5 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section3_p1") }} />
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-8 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section3_p2") }} />
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-bold text-red-600 uppercase tracking-wider">Planner Dashboard</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Route Planning</span>
              <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-wider">Service Portal</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Freeflow of Information */}
      <section className="py-24 px-6 bg-slate-50/50 overflow-hidden">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Text — left */}
          <div className="lg:col-span-7 order-2 lg:order-1 pt-4">
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{t("section4_tag")}</span>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1] mb-8">
              <span className="font-extralight text-slate-400">{t("section4_line1")}</span><br />
              <span className="font-black text-slate-900">{t("section4_line2")}</span>{" "}
              <span className="font-black text-red-600">{t("section4_line3")}</span>
            </h2>
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-5 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section4_p1") }} />
            <p className="text-base text-slate-500 font-light leading-[1.8] mb-8 max-w-lg" dangerouslySetInnerHTML={{ __html: t("section4_p2") }} />
            <button onClick={() => nav("/auth")}
              className="btn-industrial px-8 py-4 bg-red-600 text-white rounded-xl text-sm font-black shadow-lg hover:bg-red-500 hover:shadow-xl transition-all">
              {t("get_started_free")} <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          {/* Image — right */}
          <div className="lg:col-span-5 relative order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-2xl shadow-xl w-full aspect-[4/5]">
              <img src={EDITORIAL_IMAGES.freeflow} alt="Operations overview showing real-time data flow between field crews, drivers, and planners on Pigeon Supply" className="w-full h-full object-cover saturate-[1.3]" style={{ filter: "contrast(1.1)" }} />
              <div className="absolute inset-0 pointer-events-none" style={{
                backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)",
                maskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)"
              }} />
            </div>
            <div className="absolute -bottom-3 -left-3 w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
            <Star className="w-8 h-8 text-white" />
          </div>
          <blockquote className="text-2xl md:text-3xl tracking-tight leading-snug mb-8">
            <span className="font-light text-slate-400">"</span>
            <span className="font-bold text-slate-900" dangerouslySetInnerHTML={{ __html: t("testimonial") }} />
            <span className="font-light text-slate-400">"</span>
          </blockquote>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">{t("testimonial_author")}</p>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className="py-28 px-6 bg-slate-50 border-y border-slate-200" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl tracking-tighter mb-4">
              <span className="font-light text-slate-400">{t("choose_your_plan").split(" ").slice(0, -1).join(" ")}</span>{" "}
              <span className="font-black text-slate-900">{t("choose_your_plan").split(" ").slice(-1)[0]}</span>
            </h2>
            <p className="text-base text-slate-400 font-light">{t("pricing_desc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <div key={plan.id} data-testid={`price-card-${plan.id}`}
                className={`relative p-8 rounded-2xl border-2 transition-all animate-fadeInUp ${plan.highlight ? "bg-white border-red-400 shadow-lg" : "bg-white border-slate-200 hover:border-slate-300"}`}
                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider">
                    {t("best_value")}
                  </div>
                )}
                <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">{plan.name}</h3>
                <div className="mt-4 mb-6 flex items-end gap-1">
                  {plan.price === 0 ? (
                    <span className="text-4xl font-black text-red-600 font-mono">Free</span>
                  ) : (
                    <>
                      <span className="text-4xl font-black text-slate-900 font-mono">{"\u20AC"}{plan.price}</span>
                      <span className="text-sm text-slate-400 font-bold mb-1">{plan.period}</span>
                    </>
                  )}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                      <Check className="w-4 h-4 text-red-500 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {plan.locked && plan.locked.length > 0 && (
                  <div className="mb-6 pt-4 border-t border-slate-200">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">{t("upgrade_to_unlock")}</p>
                    <ul className="space-y-1.5">
                      {plan.locked.map((f, fi) => (
                        <li key={fi} className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                          <Lock className="w-3 h-3 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button data-testid={`checkout-${plan.id}`} onClick={() => plan.price === 0 ? nav("/portal") : handleCheckout(plan.id)}
                  className={`btn-industrial w-full py-4 rounded-xl text-sm font-black ${plan.highlight ? "bg-red-600 text-white shadow-md hover:bg-red-500" : "bg-slate-100 text-slate-800 hover:bg-slate-200"}`}>
                  {plan.cta || "Get Started"} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl tracking-tighter mb-4">
            <span className="font-light text-red-200">{t("cta_line1")}</span><br />
            <span className="font-black text-white">{t("cta_line2")}</span>
          </h2>
          <p className="text-lg text-red-100/70 font-light mb-10">{t("cta_desc")}</p>
          <button data-testid="cta-start-btn" onClick={() => nav("/auth")}
            className="btn-industrial px-12 py-5 bg-white text-red-600 rounded-xl text-sm font-black shadow-2xl hover:bg-red-50 transition-all">
            {t("launch_pigeon")} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center"><Bird className="w-4 h-4 text-white" /></div>
                <span className="text-sm font-black uppercase tracking-tight">Pigeon Supply</span>
              </div>
              <p className="text-xs text-slate-400 font-medium leading-relaxed">{t("footer_desc")}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("product")}</p>
              <ul className="space-y-2">
                <li><button onClick={() => pricingRef.current?.scrollIntoView({behavior:"smooth"})} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("nav_pricing")}</button></li>
                <li><button onClick={() => nav("/")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">Demo</button></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("legal")}</p>
              <ul className="space-y-2">
                <li><button data-testid="footer-terms" onClick={() => setActivePage("terms")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("terms_of_service")}</button></li>
                <li><button data-testid="footer-privacy" onClick={() => setActivePage("privacy")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("privacy_policy")}</button></li>
                <li><button data-testid="footer-refund" onClick={() => setActivePage("refund")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("refund_policy")}</button></li>
                <li><button data-testid="footer-gdpr" onClick={() => setActivePage("gdpr")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("gdpr_cookies")}</button></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("support")}</p>
              <ul className="space-y-2">
                <li><button data-testid="footer-contact" onClick={() => setActivePage("contact")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("contact_us")}</button></li>
                <li><a href="mailto:support@onepigeon.app" className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">support@onepigeon.app</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">&copy; 2026 Pigeon Supply. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Lock className="w-4 h-4 text-slate-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Secured by Stripe</span>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/120px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-5 opacity-40" />
            </div>
          </div>
        </div>
      </footer>

      {/* GDPR Cookie Consent Banner */}
      {!cookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-banner">
          <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-2xl shadow-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
            <Cookie className="w-8 h-8 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-bold mb-1">We value your privacy</h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                Pigeon Supply uses only essential cookies required for the platform to function. We do not use tracking or advertising cookies. By continuing, you agree to our{" "}
                <button onClick={() => setActivePage("privacy")} className="text-red-400 underline hover:text-red-300">Privacy Policy</button> and{" "}
                <button onClick={() => setActivePage("terms")} className="text-red-400 underline hover:text-red-300">Terms of Service</button>.
                GDPR compliant. Data stored within the EU.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                data-testid="cookie-accept-btn"
                onClick={() => { localStorage.setItem("pigeon-cookie-consent", "accepted"); setCookieConsent("accepted"); }}
                className="px-5 py-2.5 bg-red-600 text-slate-950 rounded-xl text-xs font-bold hover:bg-red-500 transition-colors"
              >
                Accept
              </button>
              <button
                data-testid="cookie-dismiss-btn"
                onClick={() => { localStorage.setItem("pigeon-cookie-consent", "dismissed"); setCookieConsent("dismissed"); }}
                className="p-2.5 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalPage({ title, onBack, content }) {
  return (
    <div className="min-h-[100dvh] bg-white" data-testid="legal-page">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center"><Bird className="w-4 h-4 text-slate-950" /></div>
            <span className="text-sm font-black uppercase tracking-tight">Pigeon Supply</span>
          </div>
          <button onClick={onBack} className="text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-red-600 transition-colors flex items-center gap-1">
            <ChevronRight className="w-3 h-3 rotate-180" /> Back
          </button>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{title}</h1>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-10">Last updated: January 2026</p>
        <div className="prose prose-sm max-w-none text-slate-600 leading-relaxed space-y-6">
          {content}
        </div>
      </main>
    </div>
  );
}

function TermsContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">1. Agreement to Terms</h2>
      <p>By accessing or using Pigeon Supply ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">2. Description of Service</h2>
      <p>Pigeon Supply is a logistics management platform designed for construction and fleet operations. The Service provides real-time fleet tracking, driver management, fuel logistics coordination, multi-language support, and communication tools for Planners, Drivers, and Machinists.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">3. Subscriptions and Billing</h2>
      <p>The Service is offered on a subscription basis. Prices are listed in EUR and billed monthly. All payments are processed securely through Stripe. Your subscription will automatically renew unless cancelled before the renewal date. You may cancel your subscription at any time through your account settings.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">4. Free Trial</h2>
      <p>New subscribers may be eligible for a 14-day free trial. No credit card is required for the trial period. At the end of the trial, you must subscribe to continue using the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">5. Acceptable Use</h2>
      <p>You agree not to use the Service for any unlawful purpose or in violation of these Terms. You are responsible for all activity that occurs under your account. You must not share your login credentials with unauthorized parties.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">6. Intellectual Property</h2>
      <p>The Service and its original content, features, and functionality are owned by Pigeon Supply and are protected by international copyright, trademark, and other intellectual property laws.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">7. Data Ownership</h2>
      <p>You retain ownership of all data you input into the Service. We do not claim ownership of your logistics data, site information, or operational records.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">8. Limitation of Liability</h2>
      <p>In no event shall Pigeon Supply be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, resulting from your use of the Service.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">9. Termination</h2>
      <p>We may terminate or suspend your account immediately, without prior notice, if you breach these Terms. Upon termination, your right to use the Service will cease immediately.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">10. Governing Law</h2>
      <p>These Terms shall be governed by the laws of Belgium, without regard to its conflict of law provisions. Any disputes will be resolved in the courts of Belgium.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">11. Contact</h2>
      <p>For questions about these Terms, contact us at: <a href="mailto:legal@onepigeon.app" className="text-red-600">legal@onepigeon.app</a></p>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">1. Information We Collect</h2>
      <p>We collect only the minimum data necessary to provide the Service, in compliance with GDPR and Belgian privacy regulations:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account information: name, email address, company name</li>
        <li>Operational data: site names, machine identifiers, delivery records, driver names</li>
        <li>Usage data: login timestamps, feature usage (anonymized)</li>
        <li>Payment data: processed and stored exclusively by Stripe (we never store card details)</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">2. How We Use Your Data</h2>
      <p>Your data is used exclusively to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Provide and maintain the Service</li>
        <li>Process transactions and send billing notifications</li>
        <li>Improve the Service through anonymized usage analytics</li>
        <li>Communicate important updates about the Service</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">3. Data Storage and Security</h2>
      <p>All data is stored on secure servers within the European Union. We use industry-standard encryption (TLS 1.3) for data in transit and AES-256 encryption for data at rest. Access to production data is restricted to authorized personnel only.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">4. Data Retention</h2>
      <p>We retain your data for the duration of your subscription plus 30 days. After account deletion, all personal data is permanently removed within 30 days. Anonymized operational data may be retained for analytics purposes.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">5. Your Rights (GDPR)</h2>
      <p>Under GDPR, you have the right to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Access your personal data</li>
        <li>Rectify inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Object to processing of your data</li>
        <li>Withdraw consent at any time</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">6. Third-Party Services</h2>
      <p>We use the following third-party services:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe</strong>: Payment processing (PCI DSS Level 1 compliant)</li>
        <li><strong>AI Translation</strong>: Text is processed for translation purposes only and not stored by the AI provider</li>
      </ul>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">7. Cookies</h2>
      <p>We use only essential cookies required for the Service to function. We do not use tracking cookies or third-party advertising cookies.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">8. Data Protection Officer</h2>
      <p>For privacy inquiries, contact our DPO at: <a href="mailto:privacy@onepigeon.app" className="text-red-600">privacy@onepigeon.app</a></p>
    </>
  );
}

function RefundContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Refund Policy</h2>
      <p>We want you to be completely satisfied with Pigeon Supply. Here is our refund policy:</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">14-Day Free Trial</h2>
      <p>All new subscriptions include a 14-day free trial. No payment is required during the trial period. If you cancel before the trial ends, you will not be charged.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">Monthly Subscriptions</h2>
      <p>For monthly subscriptions, you may request a full refund within 7 days of your payment date if you are not satisfied with the Service. After 7 days, refunds are issued on a prorated basis for the remaining days in the billing period.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">How to Request a Refund</h2>
      <p>To request a refund, contact our support team at <a href="mailto:billing@onepigeon.app" className="text-red-600">billing@onepigeon.app</a> with your account email and reason for the refund. Refunds are processed within 5-10 business days back to the original payment method.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">Cancellation</h2>
      <p>You may cancel your subscription at any time. After cancellation, you retain access to the Service until the end of your current billing period. No further charges will be made after cancellation.</p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">Exceptions</h2>
      <p>Refunds may not be available in cases of:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Violation of our Terms of Service</li>
        <li>Fraudulent activity</li>
        <li>Subscriptions active for more than 30 days without prior refund request</li>
      </ul>
    </>
  );
}

function ContactContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">Get in Touch</h2>
      <p>We'd love to hear from you. Whether you have a question about features, pricing, or anything else, our team is ready to answer.</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <Mail className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">General Inquiries</h3>
          <a href="mailto:info@onepigeon.app" className="text-sm text-red-600 font-medium">info@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <FileText className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Billing & Payments</h3>
          <a href="mailto:billing@onepigeon.app" className="text-sm text-red-600 font-medium">billing@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <MessageSquare className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Technical Support</h3>
          <a href="mailto:support@onepigeon.app" className="text-sm text-red-600 font-medium">support@onepigeon.app</a>
        </div>
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
          <Shield className="w-6 h-6 text-red-500 mb-3" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Privacy & GDPR</h3>
          <a href="mailto:privacy@onepigeon.app" className="text-sm text-red-600 font-medium">privacy@onepigeon.app</a>
        </div>
      </div>
      
      <h2 className="text-lg font-bold text-slate-900 mt-10">Office Address</h2>
      <p className="text-slate-400">
        Pigeon Supply<br />
        Supply Management Platform<br />
        Belgium, EU
      </p>
      
      <h2 className="text-lg font-bold text-slate-900 mt-8">Business Hours</h2>
      <p className="text-slate-400">Monday - Friday: 09:00 - 18:00 CET<br />Weekend: Emergency support only</p>
    </>
  );
}


function GDPRContent() {
  return (
    <>
      <h2 className="text-lg font-bold text-slate-900 mt-8">GDPR Compliance</h2>
      <p>Pigeon Supply is fully compliant with the General Data Protection Regulation (GDPR) of the European Union. We are committed to protecting the privacy and personal data of all users.</p>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Data Controller</h2>
      <p>Pigeon Supply, registered in Belgium, EU, acts as the Data Controller for all personal data collected through the Service. Contact: <a href="mailto:privacy@onepigeon.app" className="text-red-600">privacy@onepigeon.app</a></p>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Legal Basis for Processing</h2>
      <p>We process personal data based on the following legal grounds:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Contract performance</strong>: Processing necessary to provide the Service you subscribed to</li>
        <li><strong>Legitimate interest</strong>: Service improvement through anonymized analytics</li>
        <li><strong>Consent</strong>: Marketing communications (opt-in only)</li>
        <li><strong>Legal obligation</strong>: Financial record-keeping and tax compliance</li>
      </ul>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Cookie Policy</h2>
      <p>Pigeon Supply uses only <strong>strictly necessary cookies</strong> required for the platform to function:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Session cookie</strong>: Maintains your authentication state during your visit</li>
        <li><strong>Preference cookie</strong>: Stores your language selection and cookie consent</li>
      </ul>
      <p className="mt-2">We do <strong>not</strong> use:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Third-party tracking cookies</li>
        <li>Advertising cookies</li>
        <li>Analytics cookies (all analytics are server-side and anonymized)</li>
      </ul>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Your Rights Under GDPR</h2>
      <p>As a data subject in the EU, you have the following rights:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Right of Access</strong> (Art. 15): Request a copy of your personal data</li>
        <li><strong>Right to Rectification</strong> (Art. 16): Correct inaccurate personal data</li>
        <li><strong>Right to Erasure</strong> (Art. 17): Request deletion of your personal data ("right to be forgotten")</li>
        <li><strong>Right to Data Portability</strong> (Art. 20): Receive your data in a machine-readable format</li>
        <li><strong>Right to Restrict Processing</strong> (Art. 18): Limit how we use your data</li>
        <li><strong>Right to Object</strong> (Art. 21): Object to processing based on legitimate interest</li>
        <li><strong>Right to Withdraw Consent</strong> (Art. 7): Withdraw consent at any time</li>
      </ul>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Data Protection Officer</h2>
      <p>Our Data Protection Officer can be reached at: <a href="mailto:dpo@onepigeon.app" className="text-red-600">dpo@onepigeon.app</a></p>

      <h2 className="text-lg font-bold text-slate-900 mt-8">Supervisory Authority</h2>
      <p>If you believe your data protection rights have been violated, you have the right to lodge a complaint with the Belgian Data Protection Authority (Gegevensbeschermingsautoriteit/Autorite de protection des donnees).</p>
    </>
  );
}
